import React, { useState, useCallback } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const TONE_OPTIONS = [
  { value: 'formal', labelEn: 'Formal', labelFr: 'Formel', desc: 'Professional and institutional tone', descFr: 'Ton professionnel et institutionnel' },
  { value: 'friendly', labelEn: 'Friendly', labelFr: 'Convivial', desc: 'Warm and welcoming tone', descFr: 'Ton chaleureux et accueillant' },
  { value: 'concise', labelEn: 'Concise', labelFr: 'Concis', desc: 'Brief and to the point', descFr: 'Bref et direct' },
];

const DEFAULT_TEMPLATES = {
  en: {
    greeting: 'Dear {{guestName}},',
    intro: 'Your reservation has been successfully confirmed. Here is a summary of your stay:',
    longTermNotice: '📋 Long Term Contract: A contract and checklist will be sent to you shortly. Please sign and return them before your arrival.',
    shortTermNote: 'Please ensure you check in on the specified date. Your room will be ready upon arrival.',
    closing: 'For any questions, please contact the residence administration.',
    footer: '© {{year}} Université Senghor — US Réservation Résidences',
  },
  fr: {
    greeting: 'Cher(e) {{guestName}},',
    intro: 'Votre réservation a été confirmée avec succès. Voici le récapitulatif de votre séjour :',
    longTermNotice: '📋 Contrat long terme : Un contrat et une liste de contrôle vous seront transmis prochainement. Veuillez les signer et les retourner avant votre arrivée.',
    shortTermNote: 'Veuillez vous assurer d\'arriver à la date indiquée. Votre chambre sera prête à votre arrivée.',
    closing: 'Pour toute question, veuillez contacter l\'administration de la résidence.',
    footer: '© {{year}} Université Senghor — US Réservation Résidences',
  },
};

const VARIABLES = ['{{guestName}}', '{{residentId}}', '{{room}}', '{{building}}', '{{checkIn}}', '{{checkOut}}', '{{accommodationType}}', '{{year}}'];

const SAMPLE_DATA = {
  guestName: 'Ahmed Benali',
  residentId: 'RES-001',
  room: 'F1-201',
  building: 'F1',
  checkIn: '2026-04-01',
  checkOut: '2026-07-01',
  accommodationType: 'Long Term Contract',
  year: new Date()?.getFullYear()?.toString(),
};

function interpolate(text, data) {
  return text?.replace(/\{\{(\w+)\}\}/g, (_, key) => data?.[key] || `{{${key}}}`);
}

export default function EmailTemplateEditor() {
  const { language } = useLanguage();
  const isFr = language === 'fr';

  const [activeTab, setActiveTab] = useState('editor'); // 'editor' | 'preview'
  const [previewLang, setPreviewLang] = useState('en');
  const [previewAccomType, setPreviewAccomType] = useState('long-term');
  const [selectedTone, setSelectedTone] = useState('formal');
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [editLang, setEditLang] = useState('en');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeSection, setActiveSection] = useState('greeting');

  const [conditionalBlocks, setConditionalBlocks] = useState({
    longTermEnabled: true,
    shortTermEnabled: true,
    longTermCustom: false,
    shortTermCustom: false,
  });

  const currentTemplate = templates?.[editLang];

  const updateField = useCallback((field, value) => {
    setTemplates(prev => ({
      ...prev,
      [editLang]: { ...prev?.[editLang], [field]: value },
    }));
  }, [editLang]);

  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  const handleReset = () => {
    setTemplates(DEFAULT_TEMPLATES);
    setSelectedTone('formal');
    setConditionalBlocks({ longTermEnabled: true, shortTermEnabled: true, longTermCustom: false, shortTermCustom: false });
  };

  const insertVariable = (field, variable) => {
    const textarea = document.getElementById(`field-${field}`);
    if (textarea) {
      const start = textarea?.selectionStart;
      const end = textarea?.selectionEnd;
      const current = currentTemplate?.[field] || '';
      const newVal = current?.substring(0, start) + variable + current?.substring(end);
      updateField(field, newVal);
      setTimeout(() => {
        textarea?.focus();
        textarea?.setSelectionRange(start + variable?.length, start + variable?.length);
      }, 0);
    } else {
      updateField(field, (currentTemplate?.[field] || '') + variable);
    }
  };

  const toneStyles = {
    formal: { headerBg: '#1a3a5c', accentColor: '#1d4ed8' },
    friendly: { headerBg: '#065f46', accentColor: '#059669' },
    concise: { headerBg: '#374151', accentColor: '#6b7280' },
  };

  const currentStyle = toneStyles?.[selectedTone];
  const previewTemplate = templates?.[previewLang];
  const isPreviewLongTerm = previewAccomType === 'long-term';
  const previewData = {
    ...SAMPLE_DATA,
    accommodationType: isPreviewLongTerm
      ? (previewLang === 'fr' ? 'Contrat long terme' : 'Long Term Contract')
      : (previewLang === 'fr' ? 'Hébergement court terme' : 'Short Term Accommodation'),
  };

  const sections = [
    { key: 'greeting', label: isFr ? 'Salutation' : 'Greeting', icon: 'MessageSquare' },
    { key: 'intro', label: isFr ? 'Introduction' : 'Introduction', icon: 'AlignLeft' },
    { key: 'longTermNotice', label: isFr ? 'Notice Long Terme' : 'Long-Term Notice', icon: 'FileText' },
    { key: 'shortTermNote', label: isFr ? 'Note Court Terme' : 'Short-Term Note', icon: 'CalendarDays' },
    { key: 'closing', label: isFr ? 'Clôture' : 'Closing', icon: 'CheckCircle' },
    { key: 'footer', label: isFr ? 'Pied de page' : 'Footer', icon: 'AlignJustify' },
  ];

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {isFr ? 'Éditeur de modèle d\'e-mail' : 'Email Template Editor'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isFr
                ? 'Personnalisez les e-mails de confirmation de réservation envoyés aux résidents' :'Customize reservation confirmation emails sent to residents'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            >
              <Icon name="RotateCcw" size={15} />
              {isFr ? 'Réinitialiser' : 'Reset'}
            </button>
            <button
              onClick={handleSave}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                saveSuccess
                  ? 'bg-emerald-500 text-white' :'bg-primary text-primary-foreground hover:opacity-90'
              }`}
            >
              <Icon name={saveSuccess ? 'Check' : 'Save'} size={15} />
              {saveSuccess ? (isFr ? 'Sauvegardé !' : 'Saved!') : (isFr ? 'Sauvegarder' : 'Save Template')}
            </button>
          </div>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 p-1 bg-muted rounded-xl w-fit">
          {[
            { key: 'editor', label: isFr ? 'Éditeur' : 'Editor', icon: 'PenLine' },
            { key: 'preview', label: isFr ? 'Aperçu' : 'Preview', icon: 'Eye' },
          ]?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => setActiveTab(tab?.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                activeTab === tab?.key
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>

        {activeTab === 'editor' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel: Settings */}
            <div className="space-y-5">
              {/* Language Selector */}
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Globe" size={15} className="text-muted-foreground" />
                  {isFr ? 'Langue d\'édition' : 'Editing Language'}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {['en', 'fr']?.map(lang => (
                    <button
                      key={lang}
                      onClick={() => setEditLang(lang)}
                      className={`py-2 text-sm font-semibold rounded-lg border transition-all ${
                        editLang === lang
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                      }`}
                    >
                      {lang === 'en' ? '🇬🇧 English' : '🇫🇷 Français'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tone Selector */}
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Sliders" size={15} className="text-muted-foreground" />
                  {isFr ? 'Ton de l\'e-mail' : 'Email Tone'}
                </h3>
                <div className="space-y-2">
                  {TONE_OPTIONS?.map(tone => (
                    <button
                      key={tone?.value}
                      onClick={() => setSelectedTone(tone?.value)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                        selectedTone === tone?.value
                          ? 'border-primary bg-primary/5 text-foreground'
                          : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{isFr ? tone?.labelFr : tone?.labelEn}</span>
                        {selectedTone === tone?.value && (
                          <Icon name="CheckCircle2" size={14} className="text-primary" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{isFr ? tone?.descFr : tone?.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Conditional Content Blocks */}
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Layers" size={15} className="text-muted-foreground" />
                  {isFr ? 'Blocs conditionnels' : 'Conditional Blocks'}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {isFr
                    ? 'Activez ou désactivez les blocs selon le type d\'hébergement' :'Enable or disable blocks based on accommodation type'}
                </p>

                {/* Long Term Block */}
                <div className={`rounded-lg border p-3 space-y-2 transition-all ${
                  conditionalBlocks?.longTermEnabled ? 'border-blue-200 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-800' : 'border-border bg-muted/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="FileText" size={14} className={conditionalBlocks?.longTermEnabled ? 'text-blue-600' : 'text-muted-foreground'} />
                      <span className={`text-xs font-semibold ${conditionalBlocks?.longTermEnabled ? 'text-blue-700 dark:text-blue-400' : 'text-muted-foreground'}`}>
                        {isFr ? 'Long Terme' : 'Long Term'}
                      </span>
                    </div>
                    <button
                      onClick={() => setConditionalBlocks(p => ({ ...p, longTermEnabled: !p?.longTermEnabled }))}
                      className={`relative w-9 h-5 rounded-full transition-colors ${conditionalBlocks?.longTermEnabled ? 'bg-blue-500' : 'bg-muted-foreground/30'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${conditionalBlocks?.longTermEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  {conditionalBlocks?.longTermEnabled && (
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      {isFr ? 'Affiche la notice de contrat pour les résidents long terme' : 'Shows contract notice for long-term residents'}
                    </p>
                  )}
                </div>

                {/* Short Term Block */}
                <div className={`rounded-lg border p-3 space-y-2 transition-all ${
                  conditionalBlocks?.shortTermEnabled ? 'border-emerald-200 bg-emerald-50/50 dark:bg-emerald-900/10 dark:border-emerald-800' : 'border-border bg-muted/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="CalendarDays" size={14} className={conditionalBlocks?.shortTermEnabled ? 'text-emerald-600' : 'text-muted-foreground'} />
                      <span className={`text-xs font-semibold ${conditionalBlocks?.shortTermEnabled ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                        {isFr ? 'Court Terme' : 'Short Term'}
                      </span>
                    </div>
                    <button
                      onClick={() => setConditionalBlocks(p => ({ ...p, shortTermEnabled: !p?.shortTermEnabled }))}
                      className={`relative w-9 h-5 rounded-full transition-colors ${conditionalBlocks?.shortTermEnabled ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}
                    >
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${conditionalBlocks?.shortTermEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  {conditionalBlocks?.shortTermEnabled && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">
                      {isFr ? 'Affiche la note d\'arrivée pour les résidents court terme' : 'Shows arrival note for short-term residents'}
                    </p>
                  )}
                </div>
              </div>

              {/* Variables Reference */}
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Code2" size={15} className="text-muted-foreground" />
                  {isFr ? 'Variables disponibles' : 'Available Variables'}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {isFr ? 'Cliquez pour insérer dans le champ actif' : 'Click to insert into active field'}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {VARIABLES?.map(v => (
                    <button
                      key={v}
                      onClick={() => insertVariable(activeSection, v)}
                      className="px-2 py-1 text-xs font-mono bg-muted hover:bg-primary/10 hover:text-primary border border-border rounded-md transition-colors"
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Panel: Content Editor */}
            <div className="lg:col-span-2 space-y-4">
              {/* Section Tabs */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="flex overflow-x-auto border-b border-border">
                  {sections?.map(sec => (
                    <button
                      key={sec?.key}
                      onClick={() => setActiveSection(sec?.key)}
                      className={`flex items-center gap-1.5 px-3 py-3 text-xs font-medium whitespace-nowrap border-b-2 transition-all ${
                        activeSection === sec?.key
                          ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <Icon name={sec?.icon} size={13} />
                      {sec?.label}
                      {(sec?.key === 'longTermNotice' || sec?.key === 'shortTermNote') && (
                        <span className={`ml-1 w-1.5 h-1.5 rounded-full ${
                          sec?.key === 'longTermNotice' ? (conditionalBlocks?.longTermEnabled ?'bg-blue-500' : 'bg-muted-foreground/30')
                            : (conditionalBlocks?.shortTermEnabled ? 'bg-emerald-500' : 'bg-muted-foreground/30')
                        }`} />
                      )}
                    </button>
                  ))}
                </div>

                <div className="p-5 space-y-4">
                  {/* Conditional block status banner */}
                  {activeSection === 'longTermNotice' && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                      conditionalBlocks?.longTermEnabled
                        ? 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800' :'bg-muted text-muted-foreground border border-border'
                    }`}>
                      <Icon name={conditionalBlocks?.longTermEnabled ? 'Eye' : 'EyeOff'} size={13} />
                      {conditionalBlocks?.longTermEnabled
                        ? (isFr ? 'Visible pour les résidents long terme' : 'Visible for long-term residents')
                        : (isFr ? 'Bloc désactivé — non affiché dans l\'e-mail' : 'Block disabled — not shown in email')}
                    </div>
                  )}
                  {activeSection === 'shortTermNote' && (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
                      conditionalBlocks?.shortTermEnabled
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800' :'bg-muted text-muted-foreground border border-border'
                    }`}>
                      <Icon name={conditionalBlocks?.shortTermEnabled ? 'Eye' : 'EyeOff'} size={13} />
                      {conditionalBlocks?.shortTermEnabled
                        ? (isFr ? 'Visible pour les résidents court terme' : 'Visible for short-term residents')
                        : (isFr ? 'Bloc désactivé — non affiché dans l\'e-mail' : 'Block disabled — not shown in email')}
                    </div>
                  )}

                  {/* Active Section Editor */}
                  {sections?.filter(s => s?.key === activeSection)?.map(sec => (
                    <div key={sec?.key} className="space-y-2">
                      <label className="block text-sm font-medium text-foreground">
                        {sec?.label}
                        {(sec?.key === 'longTermNotice' || sec?.key === 'shortTermNote') && (
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-normal ${
                            sec?.key === 'longTermNotice' ?'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                          }`}>
                            {sec?.key === 'longTermNotice' ? (isFr ?'Long terme uniquement' : 'Long-term only')
                              : (isFr ? 'Court terme uniquement' : 'Short-term only')}
                          </span>
                        )}
                      </label>
                      <textarea
                        id={`field-${sec?.key}`}
                        value={currentTemplate?.[sec?.key] || ''}
                        onChange={e => updateField(sec?.key, e?.target?.value)}
                        rows={sec?.key === 'greeting' || sec?.key === 'footer' ? 2 : 4}
                        className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none font-mono leading-relaxed"
                        placeholder={isFr ? `Saisissez le contenu de la section "${sec?.label}"...` : `Enter content for "${sec?.label}" section...`}
                      />
                      <p className="text-xs text-muted-foreground">
                        {isFr ? 'Utilisez les variables ci-dessus pour insérer des données dynamiques' : 'Use variables above to insert dynamic data'}
                      </p>
                    </div>
                  ))}

                  {/* Quick navigation between sections */}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <button
                      onClick={() => {
                        const idx = sections?.findIndex(s => s?.key === activeSection);
                        if (idx > 0) setActiveSection(sections?.[idx - 1]?.key);
                      }}
                      disabled={sections?.[0]?.key === activeSection}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <Icon name="ChevronLeft" size={14} />
                      {isFr ? 'Précédent' : 'Previous'}
                    </button>
                    <span className="text-xs text-muted-foreground">
                      {sections?.findIndex(s => s?.key === activeSection) + 1} / {sections?.length}
                    </span>
                    <button
                      onClick={() => {
                        const idx = sections?.findIndex(s => s?.key === activeSection);
                        if (idx < sections?.length - 1) setActiveSection(sections?.[idx + 1]?.key);
                      }}
                      disabled={sections?.[sections?.length - 1]?.key === activeSection}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      {isFr ? 'Suivant' : 'Next'}
                      <Icon name="ChevronRight" size={14} />
                    </button>
                  </div>
                </div>
              </div>

              {/* All Sections Overview */}
              <div className="bg-card border border-border rounded-xl p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="LayoutList" size={15} className="text-muted-foreground" />
                  {isFr ? 'Vue d\'ensemble des sections' : 'Sections Overview'}
                </h3>
                <div className="space-y-2">
                  {sections?.map(sec => {
                    const isConditional = sec?.key === 'longTermNotice' || sec?.key === 'shortTermNote';
                    const isEnabled = sec?.key === 'longTermNotice'
                      ? conditionalBlocks?.longTermEnabled
                      : sec?.key === 'shortTermNote'
                        ? conditionalBlocks?.shortTermEnabled
                        : true;
                    const content = currentTemplate?.[sec?.key] || '';
                    return (
                      <button
                        key={sec?.key}
                        onClick={() => setActiveSection(sec?.key)}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all ${
                          activeSection === sec?.key
                            ? 'border-primary bg-primary/5' :'border-border hover:border-foreground/20 hover:bg-muted/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Icon name={sec?.icon} size={13} className="text-muted-foreground" />
                            <span className="text-xs font-semibold text-foreground">{sec?.label}</span>
                            {isConditional && (
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                isEnabled
                                  ? sec?.key === 'longTermNotice' ?'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' :'bg-muted text-muted-foreground'
                              }`}>
                                {isEnabled ? (isFr ? 'Actif' : 'Active') : (isFr ? 'Désactivé' : 'Disabled')}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{content?.length} {isFr ? 'car.' : 'chars'}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{content || (isFr ? '(vide)' : '(empty)')}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'preview' && (
          <div className="space-y-4">
            {/* Preview Controls */}
            <div className="flex flex-wrap items-center gap-3 bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-2">
                <Icon name="Globe" size={15} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">{isFr ? 'Langue :' : 'Language:'}</span>
                <div className="flex gap-1">
                  {['en', 'fr']?.map(lang => (
                    <button
                      key={lang}
                      onClick={() => setPreviewLang(lang)}
                      className={`px-3 py-1 text-xs font-semibold rounded-lg border transition-all ${
                        previewLang === lang
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {lang === 'en' ? 'EN' : 'FR'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="w-px h-5 bg-border" />
              <div className="flex items-center gap-2">
                <Icon name="Home" size={15} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">{isFr ? 'Type :' : 'Type:'}</span>
                <div className="flex gap-1">
                  {[
                    { value: 'long-term', label: isFr ? 'Long terme' : 'Long Term', color: 'blue' },
                    { value: 'short-term', label: isFr ? 'Court terme' : 'Short Term', color: 'emerald' },
                  ]?.map(type => (
                    <button
                      key={type?.value}
                      onClick={() => setPreviewAccomType(type?.value)}
                      className={`px-3 py-1 text-xs font-semibold rounded-lg border transition-all ${
                        previewAccomType === type?.value
                          ? type?.color === 'blue' ?'bg-blue-500 text-white border-blue-500' :'bg-emerald-500 text-white border-emerald-500' :'border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {type?.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="w-px h-5 bg-border" />
              <div className="flex items-center gap-2">
                <Icon name="Palette" size={15} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">{isFr ? 'Ton :' : 'Tone:'}</span>
                <span className="text-xs px-2 py-1 bg-muted rounded-md text-muted-foreground capitalize">{selectedTone}</span>
              </div>
            </div>

            {/* Email Preview */}
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={15} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">
                    {previewLang === 'fr'
                      ? `Confirmation de réservation — ${previewData?.room} | Université Senghor`
                      : `Reservation Confirmation — ${previewData?.room} | Université Senghor`}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{isFr ? 'Aperçu en direct' : 'Live Preview'}</span>
              </div>

              <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
                <div className="max-w-xl mx-auto bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md">
                  {/* Email Header */}
                  <div className="px-8 py-6 text-center" style={{ backgroundColor: currentStyle?.headerBg }}>
                    <h1 className="text-white text-xl font-bold m-0">Université Senghor</h1>
                    <p className="text-white/80 text-sm mt-1">US Réservation Résidences</p>
                  </div>

                  {/* Email Body */}
                  <div className="px-8 py-6 space-y-4">
                    <p className="text-gray-800 dark:text-gray-200 text-sm font-medium"
                      dangerouslySetInnerHTML={{ __html: interpolate(previewTemplate?.greeting, previewData)?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                    />
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      {interpolate(previewTemplate?.intro, previewData)}
                    </p>

                    {/* Info Table */}
                    <table className="w-full text-sm border-collapse">
                      <tbody>
                        {[
                          { label: previewLang === 'fr' ? 'Nom' : 'Name', value: previewData?.guestName },
                          { label: previewLang === 'fr' ? 'ID Résident' : 'Resident ID', value: previewData?.residentId },
                          { label: previewLang === 'fr' ? 'Bâtiment' : 'Building', value: previewData?.building },
                          { label: previewLang === 'fr' ? 'Chambre' : 'Room', value: previewData?.room },
                          { label: previewLang === 'fr' ? 'Date d\'arrivée' : 'Check-in Date', value: previewData?.checkIn },
                          { label: previewLang === 'fr' ? 'Date de départ' : 'Check-out Date', value: previewData?.checkOut },
                          { label: previewLang === 'fr' ? 'Type d\'hébergement' : 'Accommodation Type', value: null, badge: true },
                        ]?.map((row, i) => (
                          <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium w-2/5">{row?.label}</td>
                            <td className="py-2 text-gray-800 dark:text-gray-200">
                              {row?.badge ? (
                                <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                  isPreviewLongTerm
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                }`}>
                                  {previewData?.accommodationType}
                                </span>
                              ) : row?.value}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Conditional: Long Term Notice */}
                    {isPreviewLongTerm && conditionalBlocks?.longTermEnabled && (
                      <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 rounded-r-lg">
                        <p className="text-amber-800 dark:text-amber-300 text-xs leading-relaxed">
                          {interpolate(previewTemplate?.longTermNotice, previewData)}
                        </p>
                      </div>
                    )}

                    {/* Conditional: Short Term Note */}
                    {!isPreviewLongTerm && conditionalBlocks?.shortTermEnabled && (
                      <div className="border-l-4 border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 rounded-r-lg">
                        <p className="text-emerald-800 dark:text-emerald-300 text-xs leading-relaxed">
                          {interpolate(previewTemplate?.shortTermNote, previewData)}
                        </p>
                      </div>
                    )}

                    <p className="text-gray-500 dark:text-gray-400 text-xs mt-4">
                      {interpolate(previewTemplate?.closing, previewData)}
                    </p>
                  </div>

                  {/* Email Footer */}
                  <div className="px-8 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-100 dark:border-gray-700 text-center">
                    <p className="text-gray-400 text-xs">
                      {interpolate(previewTemplate?.footer, previewData)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
